import React, { useState, useEffect, useContext } from "react";

import { Route, useParams, useLocation, Switch } from "react-router-dom";
import { useQuery, useMutation } from "react-apollo";

import { Title, Button, Field, Icon, Control } from "rbx";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import {
  SINGLE_COMPANY_QUERY,
  UPDATE_COMPANY_MUTATION
} from "../../../graphql/Company/Companys.graphql";

import Loading from "../../../components/Loading";

import { ourToast as toast } from "../../../utils";

import CompanyTabs from "./CompanyTabs";

import Locations from "./routes/Locations";
import Details from "./routes/Details";

import Settings from "./routes/Settings";

import { AddCompanyLocationForm } from "../../../components/Company/Locations";

import { ModalContext } from "../../../components/ModalProvider";
import { useSystemCodes } from "../../../utils/hooks";

const CompanyPage = () => {
  const { data: systemCodes } = useSystemCodes({
    Category: "General Ledger",
    CodeName: "AcctType"
  });
  const { id: CompanyId } = useParams();
  const location = useLocation();
  const [inputs, setInputs] = useState({});
  const [isLoading, setLoading] = useState(false);
  const [editing, setEditing] = useState(false);
  const { setModalOpen } = useContext(ModalContext);
  const [codes, setCodes] = useState(null);
  const { data, loading } = useQuery(SINGLE_COMPANY_QUERY, {
    variables: {
      where: {
        RecId: parseInt(CompanyId)
      }
    }
  });

  const [updateCompany] = useMutation(UPDATE_COMPANY_MUTATION, {
    refetchQueries: [
      "ALL_COMPANYS_QUERY",
      "SINGLE_COMPANY_QUERY",
      "COMPANY_PARAMETERS_QUERY"
    ]
  });
  useEffect(() => {
    if (data && data.Company) {
      setInputs({
        ...data.Company
      });
    }
  }, [data]);

  useEffect(() => {
    if (!codes && Array.isArray(systemCodes)) {
      setCodes(systemCodes);
    }
  }, [codes, systemCodes]);

  const handleChange = (name, value) => {
    setInputs(prev => ({ ...prev, [name]: value }));
  };

  const toggleEditing = () => setEditing(!editing);

  const handleSave = async () => {
    try {
      if (
        inputs?.LaborHourIncrement &&
        parseFloat(inputs?.LaborHourIncrement) < 0.01
      ) {
        toast.error("Labor Hour increment must be greater than 0.01");
      }
      setLoading(true);
      delete inputs.PrimaryStorageDimension.__typename;
      delete inputs.SecondaryStorageDimension.__typename;
      delete inputs.TertiaryStorageDimension.__typename;
      await updateCompany({
        variables: {
          data: {
            FiscalPeriodPassword: inputs.FiscalPeriodPassword,
            Name: inputs.Name,
            Address1: inputs.Address1,
            Address2: inputs.Address2,
            City: inputs.City,
            State: inputs.State,
            ZipCode: inputs.ZipCode,
            County: inputs.County,
            Country: inputs.Country,
            PhoneNo: inputs.PhoneNo,
            FaxNo: inputs.FaxNo,
            TimeZoneRecIdRef: parseInt(inputs.TimeZoneRecIdRef),
            Website: inputs.Website,
            Currency: inputs.Currency,
            Active: inputs.Active,
            ImageUrl: inputs.ImageUrl,
            NextOrderNo: inputs.NextOrderNo
              ? parseInt(inputs.NextOrderNo)
              : null,
            NextInvcNo: inputs.NextInvcNo ? parseInt(inputs.NextInvcNo) : null,
            DefTripInterval: inputs.DefTripInterval
              ? parseInt(inputs.DefTripInterval)
              : null,
            DefPickupLocationRecIdRef: inputs.DefPickupLocationRecIdRef
              ? parseInt(inputs.DefPickupLocationRecIdRef)
              : null,
            NextWONo: inputs.NextWONo ? parseInt(inputs.NextWONo) : null,
            InvCostMethRecIdRef: inputs.InvCostMethRecIdRef
              ? parseInt(inputs.InvCostMethRecIdRef)
              : null,
            InvoiceNote: inputs.InvoiceNote,
            PrimaryStorageDimension: inputs.PrimaryStorageDimension,
            SecondaryStorageDimension: inputs.SecondaryStorageDimension,
            TertiaryStorageDimension: inputs.TertiaryStorageDimension,
            UsePrimaryStorageDimension: inputs.UsePrimaryStorageDimension,
            UseSecondaryStorageDimension: inputs.UseSecondaryStorageDimension,
            UseTertiaryStorageDimension: inputs.UseTertiaryStorageDimension,
            AllowMaintenanceBilling: inputs.AllowMaintenanceBilling,
            MaxLoadTime: inputs.MaxLoadTime
              ? parseFloat(inputs.MaxLoadTime)
              : null,
            MaxUnloadTime: inputs.MaxUnloadTime
              ? parseFloat(inputs.MaxUnloadTime)
              : null,
            LaborHourIncrement: inputs.LaborHourIncrement
              ? parseFloat(inputs.LaborHourIncrement)
              : null
          },
          where: {
            RecId: parseInt(CompanyId)
          }
        }
      });
      setEditing(false);
      setLoading(false);
      toast.success("Company updated successfully");
    } catch (err) {
      setLoading(false);
      toast.error("Error updating Company.");
    }
  };
  const getActionButtons = () => {
    if (location.pathname.includes("locations")) {
      return (
        <Field kind="group">
          <Control>
            <Button
              color="success"
              size="small"
              onClick={() => {
                setModalOpen(
                  true,
                  <AddCompanyLocationForm
                    codes={codes}
                    onComplete={() => setModalOpen(false, "")}
                  />
                );
              }}
            >
              <Icon>
                <FontAwesomeIcon icon="plus" />
              </Icon>
              <span>Add Location</span>
            </Button>
          </Control>
        </Field>
      );
    }
    return (
      <Field kind="group">
        <Control>
          <Button color="primary" size="small" onClick={toggleEditing}>
            <Icon>
              <FontAwesomeIcon icon="edit" />
            </Icon>
            <span>Edit</span>
          </Button>
        </Control>
        <Control>
          <Button color="success" size="small" onClick={handleSave}>
            <Icon>
              <FontAwesomeIcon icon="save" />
            </Icon>
            <span>Save</span>
          </Button>
        </Control>
      </Field>
    );
  };
  if (loading || isLoading || !codes) return <Loading />;
  return (
    data &&
    data.Company && (
      <div className="company-page">
        <header className="page-head">
          <div className="page-head-start">
            <Title color="primary" size={3}>
              {inputs.Name}
            </Title>
          </div>
          <div className="page-head-end">{getActionButtons()}</div>
        </header>
        <CompanyTabs />
        <Switch>
          <Route path="/admin/company/:id/details">
            <Details
              editing={editing}
              inputs={inputs}
              onChange={handleChange}
            />
          </Route>
          <Route path="/admin/company/:id/locations">
            <Locations codes={codes} />
          </Route>
          <Route path="/admin/company/:id/settings">
            <Settings
              editing={editing}
              inputs={inputs}
              onChange={handleChange}
            />
          </Route>
        </Switch>
      </div>
    )
  );
};

export default CompanyPage;
